import { ReactNode, useMemo } from "react"
import { InlineSelect } from "@ui/forms/inline"
import { type LucideIcon } from "lucide-react"

import { useFetchUsers } from "@/hooks/api/users"
import { AssignedTo } from "@/components/users/assigned-to"
import { UserAvatar } from "@/components/users/user-avatar"

import { InputOption } from "../props"

export interface InlineUserSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedUserId: string | null
  setSelectedUserId: (userId: string | null) => void
  onSave?: (userId: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  avatarSize?: "sm" | "xs" | "md"
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  className?: string
  allowedUserIds?: string[]
}

export function InlineUserSelect({
  label,
  selectedUserId,
  setSelectedUserId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  avatarSize = "sm",
  hideLabel,
  LabelIcon,
  size,
  className,
  allowedUserIds,
}: InlineUserSelectProps) {
  const { data: users, isLoading } = useFetchUsers()

  const options = useMemo(() => {
    const allowed = allowedUserIds?.length ? new Set(allowedUserIds) : null
    return (
      users?.users
        ?.filter((user) => !allowed || allowed.has(user.id))
        .map((user) => ({
          value: user.id,
          label: user.full_name,
        })) || []
    )
  }, [allowedUserIds, users?.users])

  const renderUserOption = (option: InputOption) => {
    return (
      <div className="text-tertiary-foreground inline-flex min-w-0 items-center gap-0 font-medium">
        <UserAvatar userId={option?.value || ""} size={avatarSize} showName />
      </div>
    )
  }

  const renderUserComponent = (option: InputOption | null) => {
    return <AssignedTo userId={option?.value || ""} size={avatarSize} />
  }

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedUserId}
      placeholder="Assign user..."
      options={options}
      disabled={isLoading || isDisabled}
      onSave={async (value) => {
        setSelectedUserId(value)
        if (onSave) {
          await onSave(value)
        }
      }}
      hideLoader
      fixedLabelWidth={fixedLabelWidth}
      renderValue={renderUserComponent}
      renderOption={renderUserOption}
      hideLabel={hideLabel}
      size={size}
      className={className}
    />
  )
}
